import React, { useState } from 'react';
import { Calendar, Clock, X, AlertCircle, Info, Pencil, Trash2, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { postShiftSignedOff, downloadShiftReport, deleteShiftLog } from '../../../services/superAdminServices';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ShiftModal from './AddShiftModal';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import { formatDate, formatDateTime } from '../../../utils/date';

export interface ShiftData {
  id: number;
  reference_number: string;
  date: string;
  start_time: string;
  end_time: string;
  department: string | null;
  status: string;
  location: string;
  priority: string;
  created_by: string;
  description: string;
  unit_on_load: string;
  site_id?: number;
  site?: {
    id: number;
    name: string;
  } | null;
  created_at: string;
  handover_remarks?: string;
}

interface ShiftHeaderProps {
  shiftId: string;
  date: string;
  time: string;
  status: string;
  onSignOff: () => void;
  onDownload: () => void;
  isDownloading: boolean;
  onEdit: () => void;
  onDelete: () => void;
  readOnly?: boolean;
}

export function InfoHeader({
  shiftId,
  date,
  time,
  status,
  onSignOff,
  onDownload,
  isDownloading,
  onEdit,
  onDelete,
  readOnly = false,
}: ShiftHeaderProps) {
  const isSignedOff = status.toLowerCase() === 'signed off';

  return (
    <div className="flex items-start justify-between mb-4 w-full">
      <div>
        <h1 className="text-xl font-semibold text-gray-800">
          {shiftId}
          <span className={`text-sm ml-2 ${isSignedOff ? 'text-green-600' : 'text-pink-600'}`}>● {status}</span>
        </h1>

        <div className="flex items-center gap-4 text-gray-600 text-sm mt-2">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" /> {date}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" /> {time}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Edit / Delete — only for active (not-yet-signed-off) shifts */}
        {!readOnly && !isSignedOff && (
          <div className="flex items-center gap-3 mr-1">
            <button onClick={onEdit} title="Edit shift" className="text-blue-600 hover:text-blue-800">
              <Pencil size={18} />
            </button>
            <button onClick={onDelete} title="Delete shift" className="text-red-600 hover:text-red-800">
              <Trash2 size={18} />
            </button>
          </div>
        )}

        {isSignedOff && (
          <button
            onClick={onDownload}
            disabled={isDownloading}
            className={`px-6 py-2 text-white rounded-md ${isDownloading ? 'bg-red-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'}`}
          >
            {isDownloading ? (
              <div className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Downloading...
              </div>
            ) : (
              'Download'
            )}
          </button>
        )}

        {!readOnly && !isSignedOff && (
          <button onClick={onSignOff} className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">
            Sign Off
          </button>
        )}
      </div>
    </div>
  );
}

const modules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

export default function InfoDetails({
  data,
  refreshData,
  readOnly = false,
}: {
  data: ShiftData;
  refreshData: () => Promise<void>;
  readOnly?: boolean;
}) {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [handoverRemarks, setHandoverRemarks] = useState('');
  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSigningOff, setIsSigningOff] = useState(false);

  // Edit shift state
  const [isEditMode, setIsEditMode] = useState(false);

  // Delete shift state
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Helper function to check if Quill editor has meaningful content
  const isHandoverRemarksEmpty = () => {
    if (!handoverRemarks || handoverRemarks.trim() === '') return true;
    // Remove HTML tags and check if there's any actual text
    const plainText = handoverRemarks.replace(/<[^>]*>/g, '').trim();
    return plainText === '';
  };

  const quillStyles = `
  .handover-editor .ql-toolbar {
    min-height: 48px;
    display: flex;
    align-items: center;
    border: none;
    border-bottom: 1px solid #d1d5db;
  }

  .handover-editor .ql-container {
    height: 180px;
    font-size: 15px;
  }

  .handover-editor .ql-editor {
    padding: 16px;
    font-size: 15px;
  }

  .handover-editor .ql-editor.ql-blank::before {
    font-style: normal;
    color: #9ca3af;
  }

  .handover-editor .ql-toolbar.ql-snow {
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
  }

  .handover-editor .ql-container.ql-snow {
    border-left: none !important;
    border-right: none !important;
    border-bottom: none !important;
  }
`;

  const formattedDate = formatDate(data.date);
  const timeRange = `${data.start_time} - ${data.end_time}`;

  const handleSignOffClick = () => {
    setShowModal(true);
  };

  const handleConfirmSignOff = async () => {
    if (isSigningOff) return;

    setIsSigningOff(true);

    try {
      await postShiftSignedOff({
        shift_log_id: data.id,
        handover_remarks: handoverRemarks,
      });

      setShowModal(false);
      setHandoverRemarks('');

      await refreshData();
    } catch (error: any) {
      console.error('Failed to sign off shift:', error);

      const message =
        error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to sign off shift. Please try again.';

      setApiError(message);
      setShowError(true);
      setShowModal(false);

      setTimeout(() => setShowError(false), 5000);

      // Allow retry if API fails
      setIsSigningOff(false);
    }
  };

  const handleCancelSignOff = () => {
    setShowModal(false);
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);

      const pdfBlob = await downloadShiftReport(data.id);

      const url = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));

      const link = document.createElement('a');
      link.href = url;

      link.setAttribute('download', `${data.reference_number}.pdf`);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);

      setApiError('Failed to download report.');
      setShowError(true);

      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteShiftLog(data.id);
      // Nothing left on this page to refresh — the record is gone, so go back.
      navigate(-1);
    } catch (error: any) {
      console.error('Failed to delete shift:', error);
      const message =
        error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to delete shift. Please try again.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  return (
    <>
      <style>{quillStyles}</style>
      <div className="mt-6 text-gray-700 text-sm leading-relaxed">
        <InfoHeader
          shiftId={data.reference_number}
          date={formattedDate}
          time={timeRange}
          status={data.status}
          onSignOff={handleSignOffClick}
          onDownload={handleDownload}
          isDownloading={isDownloading}
          onEdit={() => setIsEditMode(true)}
          onDelete={() => setIsDeleteOpen(true)}
          readOnly={readOnly}
        />

        <div className="space-y-6">
          {/* Shift Description */}
          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-2">Shift Description</h3>

            <div
              className="text-gray-700 max-w-[750px] w-full overflow-x-hidden break-words [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6"
              dangerouslySetInnerHTML={{
                __html: data.description || '-',
              }}
            />
          </div>

          {/* Handover Remark */}
          {data.handover_remarks && (
            <div>
              <h3 className="text-base font-semibold text-gray-800 mb-2">Handover Remark</h3>

              <div
                className="text-gray-700 max-w-[750px] w-full overflow-x-hidden break-words [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6"
                dangerouslySetInnerHTML={{
                  __html: data.handover_remarks,
                }}
              />
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-gray-500 mb-2">Site</p>
            <div className="flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-gray-400" />
              <span className="font-semibold text-gray-900">{data.site?.name || '-'}</span>
            </div>
          </div>

          <div>
            <p className="text-gray-500">Units on Load</p>
            <p className="font-semibold">{data.unit_on_load}</p>
          </div>
        </div>

        <div className="mt-4 grid gap-4">
          {/*<div>
            <p className="text-gray-500">Location</p>
            <p className="font-semibold">{data.location}</p>
          </div>
          <div>
            <p className="text-gray-500">Priority</p>
            <p className="font-semibold">{data.priority}</p>
          </div>
          <div>
            <p className="text-gray-500">Created By</p>
            <p className="font-semibold">{data.created_by}</p>
          </div>
          <div>
            <p className="text-gray-500">Created At</p>
            <p className="font-semibold">{formatDateTime(data.created_at)}</p>
          </div>*/}
        </div>
      </div>

      {/* Sign Off Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-[1050px] rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.15)] relative">
            {/* Close Button */}
            <button onClick={handleCancelSignOff} className="absolute right-8 top-8 text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>

            <div className="px-12 pt-12 pb-10">
              {/* Title */}
              <h2 className="text-[20px] font-semibold text-[#1F2937] mb-8">Sign Off Shift</h2>

              {/* Label */}
              <div className="flex items-center gap-1 mb-3">
                <label className="text-sm font-medium text-[#374151]">Handover Remark</label>
                <Info className="w-4 h-4 text-gray-400" />
                <span className="text-red-500">*</span>
              </div>

              <div className="border border-gray-300 rounded-md overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={handoverRemarks}
                  onChange={setHandoverRemarks}
                  modules={modules}
                  placeholder="Add description here"
                  className="handover-editor"
                />
              </div>
              {/* Footer */}
              <div className="flex justify-end gap-3 mt-12">
                <button
                  onClick={handleCancelSignOff}
                  className="
              h-10
              px-7
              border
              border-gray-300
              rounded-md
              text-gray-600
              bg-white
              hover:bg-gray-50
            "
                >
                  Cancel
                </button>

                <button
                  onClick={handleConfirmSignOff}
                  disabled={isHandoverRemarksEmpty() || isSigningOff}
                  className="
              h-10
              px-9
              rounded-md
              bg-red-500
              text-white
              hover:bg-red-600
              disabled:bg-red-300
              disabled:cursor-not-allowed
              disabled:hover:bg-red-300
            "
                >
                  {isSigningOff ? 'Signing Off...' : 'Yes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Shift Modal */}
      {isEditMode && (
        <ShiftModal
          shiftId={data.id}
          isEdit={true}
          onClose={() => setIsEditMode(false)}
          onSave={async () => {
            setIsEditMode(false);
            await refreshData();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteOpen}
        title="Are you sure you want to delete this shift log?"
        message=""
        confirmText="Yes, Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        onCancel={() => {
          if (isDeleting) return;
          setIsDeleteOpen(false);
        }}
        onConfirm={handleDeleteConfirm}
      />

      {/* Error Notification */}
      {showError && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative animate-slide-in">
            <button onClick={() => setShowError(false)} className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Error</h4>
                <p className="text-sm">{apiError}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
